import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Pencil, Check, X } from "lucide-react";
import { TextField } from "@/components/FormField";
import { formatText, formatDays, formatCurrencyInput, parseCurrency } from "@/lib/format";

const INIT = {
  nombreRazonSocial: "", rfc: "", nombreContacto: "", telefono: "", whatsapp: "", email: "",
  calle: "", noExterior: "", noInterior: "", colonia: "", municipioAlcaldia: "", estado: "", codigoPostal: "",
  entregaUsarDatosCliente: false, entregaRazonSocial: "", entregaNombreRecibe: "", entregaCalleNumero: "",
  entregaColonia: "", entregaMunicipio: "", entregaEstado: "", entregaCodigoPostal: "", entregaTelefono: "",
  entregaUbicacionMaps: "", listaPrecios: "", condicionesPago: "", tiempoEntrega: "", vigenciaCotizacion: "",
  limiteCredito: "", fechaAutorizacion: "", vendedor: "", autorizo: "", diasCredito: "", recordatorioActivado: true,
  descuentoTipo: "", descuentoPorcentaje: "", descuentos: [], tipoPersona: "fisica",
};

const mapSolicitud = (s) => ({
  ...INIT,
  nombreRazonSocial: s.nombre_razon_social || "", rfc: s.rfc || "", nombreContacto: s.nombre_contacto || "",
  telefono: s.telefono || "", whatsapp: s.whatsapp || "", email: s.email || "",
  calle: s.calle || "", noExterior: s.no_exterior || "", noInterior: s.no_interior || "",
  colonia: s.colonia || "", municipioAlcaldia: s.municipio_alcaldia || "", estado: s.estado || "", codigoPostal: s.codigo_postal || "",
  entregaNombreRecibe: s.entrega_nombre_recibe || "", entregaCalleNumero: s.entrega_calle_numero || "",
  entregaColonia: s.entrega_colonia || "", entregaMunicipio: s.entrega_municipio || "", entregaEstado: s.entrega_estado || "",
  entregaTelefono: s.entrega_telefono || "", entregaUbicacionMaps: s.entrega_ubicacion_maps || "",
  condicionesPago: s.solicita_credito ? "Crédito" : "Contado",
  limiteCredito: s.limite_credito ? String(s.limite_credito) : "",
  diasCredito: s.dias_credito ? String(s.dias_credito) : "",
  tipoPersona: s.tipo_persona || "fisica",
});

export default function AltaClientes() {
  const navigate = useNavigate();
  const location = useLocation();
  const solicitud = location.state?.solicitud || null;
  const { isAdmin } = useAuth();
  const [formData, setFormData] = useState(solicitud ? mapSolicitud(solicitud) : INIT);
  const [priceLists, setPriceLists] = useState([]);
  const [users, setUsers] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDescTipo, setEditDescTipo] = useState(null);
  const [editDescPct, setEditDescPct] = useState("");

  const cargarCatalogos = () => {
    Promise.all([api.get("/price-lists"), api.get("/users"), api.get("/descuentos")]).then(([pl, us, ds]) => {
      setPriceLists(pl.data || []);
      setUsers(us.data || []);
      setCatalogo((ds.data || []).filter((d) => d.activo));
    });
  };

  useEffect(() => {
    cargarCatalogos();
    const onFocus = () => cargarCatalogos();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const set = (id, value) => setFormData((p) => ({ ...p, [id]: value }));

  // Si "Usar datos del cliente" está activo, re-sincroniza los datos de entrega
  // cada vez que cambian los datos de las secciones 1 y 2.
  useEffect(() => {
    if (!formData.entregaUsarDatosCliente) return;
    setFormData((prev) => ({
      ...prev,
      entregaRazonSocial: prev.nombreRazonSocial,
      entregaNombreRecibe: prev.nombreContacto,
      entregaCalleNumero: `${prev.calle} ${prev.noExterior} ${prev.noInterior || ""}`.replace(/\s+/g, " ").trim(),
      entregaColonia: prev.colonia,
      entregaMunicipio: prev.municipioAlcaldia,
      entregaEstado: prev.estado,
      entregaCodigoPostal: prev.codigoPostal,
      entregaTelefono: prev.telefono,
    }));
  }, [
    formData.entregaUsarDatosCliente, formData.nombreRazonSocial, formData.nombreContacto,
    formData.calle, formData.noExterior, formData.noInterior, formData.colonia,
    formData.municipioAlcaldia, formData.estado, formData.codigoPostal, formData.telefono,
  ]);

  const onText = (e) => set(e.target.id, formatText(e.target.id, e.target.value));
  const onDays = (e) => set(e.target.id, formatDays(e.target.id, e.target.value, formData[e.target.id] || ""));
  const onCurrency = (e) => set(e.target.id, formatCurrencyInput(e.target.value));
  const fld = (id, label, opts = {}) => (
    <TextField id={id} label={label} value={formData[id]} onChange={opts.onChange || onText} type={opts.type} readOnly={opts.readOnly} required={opts.req} testid={`cliente-${id}`} />
  );

  const handleCheckbox = (checked) => {
    setFormData((prev) => {
      const n = { ...prev, entregaUsarDatosCliente: checked };
      if (checked) {
        n.entregaRazonSocial = prev.nombreRazonSocial;
        n.entregaNombreRecibe = prev.nombreContacto;
        n.entregaCalleNumero = `${prev.calle} ${prev.noExterior} ${prev.noInterior || ""}`.replace(/\s+/g, " ").trim();
        n.entregaColonia = prev.colonia; n.entregaMunicipio = prev.municipioAlcaldia;
        n.entregaEstado = prev.estado; n.entregaCodigoPostal = prev.codigoPostal; n.entregaTelefono = prev.telefono;
      } else {
        ["entregaRazonSocial", "entregaNombreRecibe", "entregaCalleNumero", "entregaColonia", "entregaMunicipio", "entregaEstado", "entregaCodigoPostal", "entregaTelefono"].forEach((k) => (n[k] = ""));
      }
      return n;
    });
  };

  const addDescuento = () => {
    if (!formData.descuentoTipo) return toast.error("Seleccione un descuento");
    if (formData.descuentos.some((d) => d.tipo === formData.descuentoTipo)) return toast.error("Este descuento ya fue agregado");
    const cat = catalogo.find((c) => c.id === formData.descuentoTipo);
    if (!cat) return;
    setFormData((prev) => ({
      ...prev,
      descuentos: [...prev.descuentos, { tipo: cat.id, tipoLabel: cat.nombre, porcentaje: cat.porcentaje }],
      descuentoTipo: "",
    }));
  };
  const removeDescuento = (tipo) => setFormData((prev) => ({ ...prev, descuentos: prev.descuentos.filter((d) => d.tipo !== tipo) }));
  const startEditDescuento = (d) => { setEditDescTipo(d.tipo); setEditDescPct(String(d.porcentaje ?? "")); };
  const cancelEditDescuento = () => { setEditDescTipo(null); setEditDescPct(""); };
  const saveEditDescuento = (tipo) => {
    setFormData((prev) => ({ ...prev, descuentos: prev.descuentos.map((d) => (d.tipo === tipo ? { ...d, porcentaje: Number(editDescPct) || 0 } : d)) }));
    setEditDescTipo(null); setEditDescPct("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombreRazonSocial || !formData.rfc || !formData.email || !formData.calle) {
      return toast.error("Por favor completa todos los campos obligatorios.");
    }
    setLoading(true);
    try {
      const payload = {
        nombre_razon_social: formData.nombreRazonSocial, rfc: formData.rfc, nombre_contacto: formData.nombreContacto,
        telefono: formData.telefono, whatsapp: formData.whatsapp, email: formData.email,
        calle: formData.calle, no_exterior: formData.noExterior, no_interior: formData.noInterior,
        colonia: formData.colonia, municipio_alcaldia: formData.municipioAlcaldia, estado: formData.estado, codigo_postal: formData.codigoPostal,
        entrega_usar_datos_cliente: formData.entregaUsarDatosCliente, entrega_razon_social: formData.entregaRazonSocial,
        entrega_nombre_recibe: formData.entregaNombreRecibe, entrega_calle_numero: formData.entregaCalleNumero,
        entrega_colonia: formData.entregaColonia, entrega_municipio: formData.entregaMunicipio, entrega_estado: formData.entregaEstado,
        entrega_codigo_postal: formData.entregaCodigoPostal, entrega_telefono: formData.entregaTelefono, entrega_ubicacion_maps: formData.entregaUbicacionMaps,
        lista_precios: formData.listaPrecios, condiciones_pago: formData.condicionesPago, tiempo_entrega: formData.tiempoEntrega,
        vigencia_cotizacion: formData.vigenciaCotizacion, limite_credito: parseCurrency(formData.limiteCredito),
        dias_credito: formData.diasCredito ? Number(formData.diasCredito) : null,
        fecha_autorizacion: formData.fechaAutorizacion || null, vendedor_asignado_id: formData.vendedor || null, autorizo: formData.autorizo,
        recordatorio_activado: formData.recordatorioActivado,
        tipo_persona: formData.tipoPersona,
        credito_estatus: formData.condicionesPago === "Crédito" ? "pendiente_firma" : null,
        descuentos: formData.descuentos,
      };
      const { data } = await api.post("/clientes", payload);
      if (solicitud?.id) {
        try { await api.post(`/solicitudes/${solicitud.id}/vincular?cliente_id=${data.id}`); } catch (_) { /* noop */ }
      }
      toast.success(`Cliente registrado (${data.id_cliente})`);
      navigate("/lista-clientes");
    } catch (err) {
      toast.error(`Error al registrar: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) return <div className="p-8 text-center text-red-500 font-semibold">Acceso no autorizado</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="alta-clientes-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Alta de clientes</h1>
        <p className="text-muted-foreground mt-2">Registra un nuevo cliente o prospecto.</p>
      </div>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            <section className="space-y-4">
              <h3 className="text-lg font-semibold">1. Datos del Cliente</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fld("nombreRazonSocial", "Nombre del Cliente / Razón Social", { req: true })}
                {fld("rfc", "RFC", { req: true })}
                {fld("nombreContacto", "Nombre del Contacto", { req: true })}
                {fld("email", "Email", { type: "email", req: true })}
                {fld("telefono", "Teléfono", { req: true })}
                {fld("whatsapp", "WhatsApp")}
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-lg font-semibold">2. Dirección Fiscal</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">{fld("calle", "Calle", { req: true })}</div>
                {fld("noExterior", "No. Exterior", { req: true })}
                {fld("noInterior", "No. Interior")}
                {fld("colonia", "Colonia", { req: true })}
                {fld("municipioAlcaldia", "Municipio / Alcaldía", { req: true })}
                {fld("estado", "Estado", { req: true })}
                {fld("codigoPostal", "Código Postal", { req: true })}
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-lg font-semibold">3. Información de Entrega</h3>
              <div className="flex items-center space-x-2 bg-muted/50 p-4 rounded-md">
                <Checkbox id="entregaUsarDatosCliente" checked={formData.entregaUsarDatosCliente} onCheckedChange={handleCheckbox} />
                <Label htmlFor="entregaUsarDatosCliente" className="cursor-pointer">Usar datos del cliente</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fld("entregaRazonSocial", "Razón Social", { readOnly: formData.entregaUsarDatosCliente })}
                {fld("entregaNombreRecibe", "Nombre de quien recibe", { readOnly: formData.entregaUsarDatosCliente })}
                <div className="md:col-span-2">{fld("entregaCalleNumero", "Calle y Número", { readOnly: formData.entregaUsarDatosCliente })}</div>
                {fld("entregaColonia", "Colonia", { readOnly: formData.entregaUsarDatosCliente })}
                {fld("entregaMunicipio", "Municipio", { readOnly: formData.entregaUsarDatosCliente })}
                {fld("entregaEstado", "Estado", { readOnly: formData.entregaUsarDatosCliente })}
                {fld("entregaCodigoPostal", "Código Postal", { readOnly: formData.entregaUsarDatosCliente })}
                {fld("entregaTelefono", "Teléfono", { readOnly: formData.entregaUsarDatosCliente })}
                {fld("entregaUbicacionMaps", "Ubicación Google Maps (URL)")}
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-lg font-semibold">4. Condiciones Comerciales</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de persona</Label>
                  <Select value={formData.tipoPersona} onValueChange={(v) => set("tipoPersona", v)}>
                    <SelectTrigger data-testid="cliente-tipoPersona"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="fisica">Persona física</SelectItem><SelectItem value="moral">Persona moral</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Lista de Precios</Label>
                  <Select value={formData.listaPrecios} onValueChange={(v) => set("listaPrecios", v)}>
                    <SelectTrigger data-testid="cliente-listaPrecios"><SelectValue placeholder="Seleccione lista" /></SelectTrigger>
                    <SelectContent>{priceLists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Condiciones de Pago</Label>
                  <Select value={formData.condicionesPago} onValueChange={(v) => set("condicionesPago", v)}>
                    <SelectTrigger data-testid="cliente-condicionesPago"><SelectValue placeholder="Seleccione condición" /></SelectTrigger>
                    <SelectContent><SelectItem value="Contado">Contado</SelectItem><SelectItem value="Crédito">Crédito</SelectItem></SelectContent>
                  </Select>
                </div>
                {fld("tiempoEntrega", "Tiempo de Entrega", { onChange: onDays })}
                {fld("vigenciaCotizacion", "Vigencia de Cotización", { onChange: onDays })}
                {fld("limiteCredito", "Límite de Crédito", { onChange: onCurrency })}
                <div className="space-y-2">
                  <Label htmlFor="diasCredito">Días de crédito</Label>
                  <Input id="diasCredito" type="number" min="0" value={formData.diasCredito} onChange={(e) => set("diasCredito", e.target.value)} data-testid="cliente-diasCredito" />
                </div>
                {fld("fechaAutorizacion", "Fecha de autorización", { type: "date", onChange: (e) => set("fechaAutorizacion", e.target.value) })}
                <div className="space-y-2">
                  <Label>Vendedor</Label>
                  <Select value={formData.vendedor} onValueChange={(v) => set("vendedor", v)}>
                    <SelectTrigger data-testid="cliente-vendedor"><SelectValue placeholder="Seleccione vendedor" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>ID</Label>
                  <Input value={users.find((u) => u.id === formData.vendedor)?.numero_identificacion || ""} readOnly disabled className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>Quién lo autorizó</Label>
                  <Select value={formData.autorizo} onValueChange={(v) => set("autorizo", v)}>
                    <SelectTrigger data-testid="cliente-autorizo"><SelectValue placeholder="Seleccione usuario" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="recordatorioActivado">Recordatorio de vencimiento (WhatsApp)</Label>
                  <div className="flex items-center gap-2 h-10">
                    <Switch id="recordatorioActivado" checked={formData.recordatorioActivado} onCheckedChange={(v) => set("recordatorioActivado", v)} data-testid="cliente-recordatorio" />
                    <span className="text-sm text-muted-foreground">{formData.recordatorioActivado ? "Activado" : "Desactivado"}</span>
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-lg font-semibold">5. Descuentos</h3>
              <div className="flex flex-col md:flex-row items-end gap-4 mb-4">
                <div className="space-y-2 flex-1">
                  <Label>Descuento</Label>
                  <Select value={formData.descuentoTipo} onValueChange={(v) => set("descuentoTipo", v)}>
                    <SelectTrigger data-testid="descuento-select"><SelectValue placeholder="Seleccione un descuento" /></SelectTrigger>
                    <SelectContent>
                      {catalogo.length === 0
                        ? <div className="px-3 py-2 text-sm text-muted-foreground">No hay descuentos cargados. Créelos en Configuración → Descuentos.</div>
                        : catalogo.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre} — {c.porcentaje}%</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="button" onClick={addDescuento} variant="secondary" data-testid="add-descuento-btn">Agregar</Button>
              </div>
              {formData.descuentos.length > 0 ? (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader><TableRow><TableHead>Tipo de Descuento</TableHead><TableHead>Porcentaje</TableHead><TableHead className="w-[100px] text-right">Acciones</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {formData.descuentos.map((d) => (
                        <TableRow key={d.tipo}>
                          <TableCell className="font-medium">{d.tipoLabel}</TableCell>
                          <TableCell>
                            {editDescTipo === d.tipo ? (
                              <Input type="number" min="0" step="0.01" value={editDescPct} onChange={(e) => setEditDescPct(e.target.value)} className="h-8 w-24" data-testid={`edit-desc-input-${d.tipo}`} autoFocus />
                            ) : (
                              `${d.porcentaje}%`
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {editDescTipo === d.tipo ? (
                                <>
                                  <Button variant="ghost" size="icon" type="button" title="Guardar" onClick={() => saveEditDescuento(d.tipo)} className="text-emerald-600" data-testid={`save-desc-${d.tipo}`}><Check className="h-4 w-4" /></Button>
                                  <Button variant="ghost" size="icon" type="button" title="Cancelar" onClick={cancelEditDescuento} data-testid={`cancel-desc-${d.tipo}`}><X className="h-4 w-4" /></Button>
                                </>
                              ) : (
                                <>
                                  <Button variant="ghost" size="icon" type="button" title="Editar" onClick={() => startEditDescuento(d)} data-testid={`edit-desc-${d.tipo}`}><Pencil className="h-4 w-4" /></Button>
                                  <Button variant="ghost" size="icon" type="button" title="Eliminar" onClick={() => removeDescuento(d.tipo)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : <p className="text-sm text-muted-foreground">No hay descuentos asignados.</p>}
            </section>

            <div className="flex justify-end gap-3 border-t pt-4">
              <Button type="button" variant="outline" onClick={() => navigate("/lista-clientes")}>Cancelar</Button>
              <Button type="submit" disabled={loading} data-testid="cliente-submit-btn">{loading ? "Guardando..." : "Registrar Cliente"}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
